import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ViewEncapsulation, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UsersService } from '../../services/requests/users.service';
import { StopsService } from '../../services/requests/stops.service';
import { ModalClickOutsideService } from '../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class UsersComponent implements OnInit, OnDestroy, AfterViewInit {
  matchedColumns: string[] = [];
  users: any[] = [];
  filtered: any[] = [];
  sortField: string = 'identifiant';
  sortDirection: 'asc' | 'desc' = 'asc';
  user = {
    identifiant: '',
    password: '',
    typeAccount: '',
    activatedStop: false,
    addCredit: false,
    initialCredit: 0,
    activateStop: false,
    nonDeletable: false
  };
  loading: boolean = false;
  credit = 0;
  totalCredits = 0; 
  isAdding: boolean = false;
  showAddUserModal = false; 
  showModalPassword = false;
  showDeleteModal = false;
  userToDelete: any = null;
  modalConfig: any = {
    title: 'Supprimer l\'utilisateur',
    confirmText: 'Supprimer',
    cancelText: 'Annuler',
    confirmButtonClass: 'modal-btn-delete',
    icon: 'warning',
    iconColor: 'red',
    warningMessage: '<p class="font-medium mb-1">Cette action est irréversible.</p><p class="text-red-700">Toutes les données associées à cet utilisateur seront définitivement supprimées.</p>',
    isLoading: false
  };

  @ViewChild('initialCreditInput') initialCreditInput!: ElementRef<HTMLInputElement>;
  @ViewChild('addUserModalContent', { static: false }) addUserModalContent!: ElementRef;

  private _searchTerm: string = '';
  get searchTerm(): string {
    return this._searchTerm;
  }
  set searchTerm(value: string) {
    this._searchTerm = value;
    this.applyFilter();
  }

  constructor(
    private route: ActivatedRoute,
    private usersService: UsersService,
    private stopsService: StopsService,
    private router: Router,
    private modalClickOutsideService: ModalClickOutsideService
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('isAdmin') !== 'true') {
      this.router.navigate(['/suivi']);
    }
    const savedSort = localStorage.getItem('localStorage_SP-GetUsers');
    if (savedSort) {
      try {
        const sortData = JSON.parse(savedSort);
        this.sortField = sortData.field || 'identifiant';
        this.sortDirection = sortData.direction || 'asc';
      } catch (e) {
        console.error('Erreur lors de la récupération du tri utilisateurs :', e);
      }
    }

    this.getUsers();
  }

  ngAfterViewInit(): void {
    this.updateModalRegistration();
  }

  private updateModalRegistration(): void {
    if (this.addUserModalContent) {
      if (this.showAddUserModal) {
        this.modalClickOutsideService.registerModal(this.addUserModalContent, () => this.closeAddUserModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.addUserModalContent);
      }
    }
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.addUserModalContent) {
      this.modalClickOutsideService.unregisterModal(this.addUserModalContent);
    }
  }

  private updateScroll(): void {
    document.body.style.overflow = (this.showAddUserModal || this.showModalPassword || this.showDeleteModal) ? 'hidden' : '';
  }

  openAddUserModal(): void {
    this.resetUserForm();
    this.showModalPassword = false;
    this.showAddUserModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  closeAddUserModal(): void {
    this.showAddUserModal = false;
    this.resetUserForm();
    this.updateScroll();
    this.updateModalRegistration();
  }

  resetUserForm(): void {
    this.user = { 
      identifiant: '', 
      password: '', 
      typeAccount: '', 
      activatedStop: false,
      addCredit: false,
      initialCredit: 0,
      activateStop: false,
      nonDeletable: false
    };
  }

  toggleModalPasswordVisibility(): void {
    this.showModalPassword = !this.showModalPassword;
    this.updateScroll();
  }

  addUser(): void {
    const { identifiant, password, typeAccount } = this.user;

    if (!identifiant || !password || !typeAccount) {
      showNotif("Veuillez remplir tous les champs requis.", 'info');
      return;
    }

    this.isAdding = true;

    const userToSend = {
      identifiant: identifiant.trim(),
      password: password.trim(),
      typeAccount,
      isOn: 'on',
      activatedStop: this.user.activateStop ?? false,
      credit: this.user.addCredit ? (this.user.initialCredit || 0) : 0,
      calculation: false,
      deletable: !this.user.nonDeletable
    };

    this.usersService.add(userToSend).subscribe({
      next: (response: any) => {
        this.isAdding = false;
        
        if (response.status) {
          showNotif(response.message || '✅ Utilisateur ajouté', 'success');
          this.closeAddUserModal();
          this.getUsers();
        } else {
          showNotif(response.message || "❌ Erreur lors de l'ajout", 'error');
        }
      },
      error: (error: any) => {
        this.isAdding = false;
        console.error("❌ Erreur API :", error);
        showNotif(error?.error?.message || "❌ Erreur lors de la création.", 'error');
      }
    });
  }

  getUsers(): void {
    this.loading = true;

    this.usersService.getAll().subscribe({
      next: (response: any) => {
        this.loading = false;

        if (response.status) {
          this.users = response.data?.users || [];
          for (let u of this.users) u.nouveauCredit = 0;
          this.calculTotal();
          this.applyFilter();
        } else {
          showNotif(response.message || "❌ Erreur lors de la récupération", 'error');
        }
      },
      error: (error: any) => {
        this.loading = false;
        console.error("Erreur getUsers() :", error);
        showNotif(error?.error?.message || "Erreur chargement utilisateurs", 'error');
      }
    });
  }

  updateCredit(id: string): void {
    const user = this.users.find(u => u._id === id);
    if (!user) return;

    this.usersService.updateCredit({ id: user._id, credit: user.nouveauCredit }).subscribe({
      next: (response: any) => {
        if (response.status) {
          const creditValue = user.nouveauCredit;
          const identifiant = user.identifiant || user._id || 'Utilisateur';
          
          if (creditValue > 0) {
            showNotif(`💰 Crédit ajouté pour ${identifiant}`, 'success');
          } else if (creditValue < 0) {
            showNotif(`💰 Crédit retiré pour ${identifiant}`, 'warning');
          } else {
            showNotif(`💰 Crédit mis à jour pour ${identifiant}`, 'update');
          }
          
          this.getUsers();
        } else {
          showNotif(response.message || "Erreur lors de la mise à jour du crédit", 'error');
        }
      },
      error: (error: any) => {
        showNotif(error?.error?.message || "Erreur serveur crédit", 'error');
      }
    });
  }

  updateCalculation(id: string): void {
    const user = this.users.find(u => u._id === id);
    if (!user) return;
    user.calculation = !user.calculation;
    this.calculTotal();
  }

  calculTotal(): void {
    this.credit = this.users
      .filter(u => u.calculation)
      .reduce((sum, u) => sum + (u.credit || 0), 0);

    const selectedEligibleUsers = this.users.filter(
      u => u.calculation && (u.typeAccount === 'client' || u.typeAccount === 'smpp')
    );

    if (selectedEligibleUsers.length > 0) {
      this.totalCredits = selectedEligibleUsers.reduce((sum, u) => sum + (u.credit || 0), 0);
    } else {
      this.totalCredits = 0;
    }
  }

  updateActivatedStop(id: string): void {
    const user = this.users.find(u => u._id === id);
    if (!user) return;

    const activatedStop = user.activatedStop === 'true' || user.activatedStop === true;

    this.usersService.updateActivatedStop({ id, activatedStop }).subscribe({
      next: (response: any) => {
        if (response.status) {
          showNotif('🔄 Stop mis à jour', 'update');
          this.getUsers();
        } else {
          showNotif(response.message || "Erreur mise à jour stop", 'error');
        }
      },
      error: (error: any) => {
        console.error("❌ Erreur updateActivatedStop :", error);
        showNotif("Erreur serveur stop", 'error');
      }
    });
  }

  openDeleteModal(user: any): void {
    this.userToDelete = user;
    this.modalConfig.message = `Êtes-vous sûr de vouloir supprimer l'utilisateur <strong class="text-gray-900">${user.identifiant}</strong> ?`;
    this.modalConfig.isLoading = false;
    this.showDeleteModal = true;
    this.updateScroll();
  }

  onModalConfirm(): void {
    if (!this.userToDelete) return;
    this.deleteUsers(this.userToDelete._id);
  }

  onModalClose(): void {
    if (!this.modalConfig.isLoading) {
      this.showDeleteModal = false;
      this.userToDelete = null;
      this.updateScroll();
    }
  }

  deleteUsers(id: string): void {
    const user = this.users.find(u => u._id === id);
    if (!user) return;

    this.modalConfig.isLoading = true;

    this.usersService.delete({ id }).subscribe({
      next: (response: any) => {
        this.modalConfig.isLoading = false;
        if (response.status) {
          this.showDeleteModal = false;
          this.updateScroll();
          showNotif('🗑️ Utilisateur supprimé', 'delete');
          this.getUsers();
        } else {
          showNotif(response.message || "❌ Erreur suppression utilisateur", 'error');
        }
      },
      error: (error: any) => {
        this.modalConfig.isLoading = false;
        console.error("❌ Erreur deleteUsers :", error);
        showNotif(error?.error?.message || "❌ Erreur serveur suppression", 'error');
      }
    });
  }

  toggleSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    
    try {
      localStorage.setItem('localStorage_SP-GetUsers', JSON.stringify({
        field: this.sortField,
        direction: this.sortDirection
      }));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du tri utilisateurs :', e);
    }
    
    this.applyFilter();
  }

  applyFilter(): void {
    const term = this._searchTerm.toLowerCase().trim();
    const matchedSet = new Set<string>();

    let base = [...this.users];

    if (term) {
      base = base.filter((u) => {
        let matched = false;
        if (u.identifiant?.toLowerCase().includes(term)) {
          matchedSet.add('Identifiant');
          matched = true;
        }
        const typeAccountLower = u.typeAccount?.toLowerCase() || '';
        const displayType = typeAccountLower === 'smpp' ? 'api' : typeAccountLower;
        if (displayType.includes(term)) {
          matchedSet.add('Type');
          matched = true;
        }
        return matched;
      });

      this.matchedColumns = Array.from(matchedSet);
    } else {
      this.matchedColumns = [];
    }

    base.sort((a, b) => {
      let valueA: any;
      let valueB: any;
      
      switch (this.sortField) {
        case 'identifiant':
          valueA = a.identifiant?.toLowerCase() || '';
          valueB = b.identifiant?.toLowerCase() || '';
          return this.sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        
        case 'type':
          valueA = a.typeAccount?.toLowerCase() || '';
          valueB = b.typeAccount?.toLowerCase() || '';
          return this.sortDirection === 'asc' 
            ? valueA.localeCompare(valueB) 
            : valueB.localeCompare(valueA);
        
        case 'credit':
          valueA = a.credit || 0;
          valueB = b.credit || 0;
          return this.sortDirection === 'asc' 
            ? valueA - valueB 
            : valueB - valueA;
        
        default:
          return 0;
      }
    });

    this.filtered = base;
  }

  getFilteredUsersCount(): number {
    return this.filtered.length;
  }

  get filteredTotalCredits(): number {
    return this.filtered.reduce((total, user) => total + (user.credit || 0), 0);
  }

  get adminCount(): number {
    return this.filtered.filter(user => user.typeAccount === 'admin').length;
  }

  clearSearch(): void {
    this.searchTerm = '';
  }

  selectAllUsers(event: any): void {
    const checked = event.target.checked;
    this.filtered.forEach(user => {
      user.calculation = checked;
    });
  }

  onCreditInputFocus(event: any): void {
    event.target.select();
  }

  onCreditInputChange(event: any, user: any): void {
    const inputValue = event.target.value;
    
    if (user.nouveauCredit === 0 && inputValue && inputValue !== '0') {
      if (inputValue.length === 1 && !isNaN(parseInt(inputValue))) {
        user.nouveauCredit = parseInt(inputValue);
        event.target.value = inputValue;
      } else if (inputValue.startsWith('-') && inputValue.length === 2 && !isNaN(parseInt(inputValue.substring(1)))) {
        user.nouveauCredit = parseInt(inputValue);
        event.target.value = inputValue;
      } else if (!isNaN(parseInt(inputValue))) {
        user.nouveauCredit = parseInt(inputValue);
        event.target.value = inputValue;
      }
    }
  }

  onAddCreditToggle(checked: boolean): void {
    this.user.addCredit = checked;
    if (checked) {
      setTimeout(() => {
        if (this.initialCreditInput) {
          const el = this.initialCreditInput.nativeElement;
          el.focus();
          el.select();
        }
      });
    }
  }

  onModalCreditFocus(event: any): void {
    event.target.select();
  }

  onModalCreditInput(event: any): void {
    const raw: string = (event.target.value || '').toString().replace(/\s+/g, '');
    if (raw === '') {
      this.user.initialCredit = 0;
      event.target.value = '';
      return;
    }

    const numeric = parseInt(raw, 10);
    if (isNaN(numeric)) {
      event.target.value = this.user.initialCredit
        ? this.formatNumber(this.user.initialCredit)
        : '';
      return;
    }

    this.user.initialCredit = numeric;
    event.target.value = this.formatNumber(numeric);
  }

  formatNumber(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '0';
    }
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      return '0';
    }
    const intValue = Math.floor(numValue);
    return intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  exporterNouveauxStops(idClient: string): void {
    this.stopsService.exportNewStops({ idClient }).subscribe(
      (response: any) => {
        if (response.status && response.data?.url) {
          window.location.href = response.data.url;
          showNotif('✅ Export des nouveaux stops réussi', 'success');
        } else {
          showNotif('❌ Erreur lors de l\'export des nouveaux stops', 'error');
        }
      },
      (error: any) => {
        console.error('Erreur export nouveaux stops:', error);
        showNotif('❌ Erreur lors de l\'export des nouveaux stops', 'error');
      }
    );
  }

  exporterTousStops(idClient: string): void {
    this.stopsService.exportAllStops({ idClient }).subscribe(
      (response: any) => {
        if (response.status && response.data?.url) {
          window.location.href = response.data.url;
          showNotif('✅ Export de tous les stops réussi', 'success');
        } else {
          showNotif('❌ Erreur lors de l\'export de tous les stops', 'error');
        }
      },
      (error: any) => {
        console.error('Erreur export tous stops:', error);
        showNotif('❌ Erreur lors de l\'export de tous les stops', 'error');
      }
    );
  }

  exporterReponses(idClient: string): void {
    this.stopsService.exportReponses({ idClient }).subscribe(
      (response: any) => {
        if (response.status && response.data?.url) {
          window.location.href = response.data.url;
          showNotif('✅ Export des réponses réussi', 'success');
        } else {
          showNotif('❌ Erreur lors de l\'export des réponses', 'error');
        }
      },
      (error: any) => {
        console.error('Erreur export réponses:', error);
        showNotif('❌ Erreur lors de l\'export des réponses', 'error');
      }
    );
  }
}
